import { useEffect, useRef } from 'react';
import * as echarts from 'echarts';
import type { ChartData } from '@/types/dashboard';

interface ChartCardProps {
  chartKey: string;
  chart: ChartData;
  fullWidth?: boolean;
}

export default function ChartCard({ chartKey, chart, fullWidth }: ChartCardProps) {
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);

  useEffect(() => {
    if (!chartRef.current) return;

    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current, undefined, { renderer: 'canvas' });
    }

    const update = () => {
      if (!chartInstance.current || !chartRef.current) return;
      const width = chartRef.current.clientWidth || 800;
      const option = buildOption(chart, width);
      chartInstance.current.setOption(option, true);
    };

    update();

    // 用 ResizeObserver 监听容器尺寸变化，重新 build 选项（移动端/桌面端布局差异大）
    const ro = new ResizeObserver(() => update());
    ro.observe(chartRef.current);

    return () => {
      ro.disconnect();
    };
  }, [chart]);

  useEffect(() => {
    return () => {
      chartInstance.current?.dispose();
      chartInstance.current = null;
    };
  }, []);

  return (
    <div className={fullWidth ? 'chart-full' : ''}>
      <div className="chart-title">{chart.title}</div>
      <div ref={chartRef} id={`chart-${chartKey}`} className="chart" />
    </div>
  );
}

const COLOR_PALETTE = [
  '#4f46e5',
  '#0ea5e9',
  '#7c3aed',
  '#d97706',
  '#059669',
  '#dc2626',
  '#0891b2',
  '#c2410c',
  '#0284c7',
  '#4338ca',
  '#9333ea',
  '#be123c',
  '#047857',
  '#b45309',
  '#2563eb',
];

const TEXT_MUTED = '#94a3b8';
const TEXT_MAIN = '#e2e8f0';
const BORDER = '#475569';
const GRID_LINE = 'rgba(148, 163, 184, 0.1)';

/** 判断键是否为时间维度 */
function isTimeKey(key: string): boolean {
  return /日期|时间|月份|年份|日期_str|date|month|time/i.test(key);
}

/** 判断值是否像日期/月份字符串（MM-DD 或 YYYY-MM-DD 等） */
function looksLikeDate(values: unknown[]): boolean {
  const sample = values.slice(0, 10).map((v) => String(v));
  return sample.every((s) => /^\d{2}-\d{2}$/.test(s) || /^\d{4}-\d{2}(-\d{2})?$/.test(s) || /^\d{4}年\d{2}月$/.test(s));
}

/** 数值格式化（保留最多两位小数，去除多余 0） */
function fmtNum(n: number): string {
  if (Number.isNaN(n)) return '-';
  if (Math.abs(n) >= 1_0000_0000) return (n / 1_0000_0000).toFixed(1) + '亿';
  if (Math.abs(n) >= 10000) return (n / 10000).toFixed(1) + '万';
  return Number.isInteger(n) ? String(n) : n.toFixed(2).replace(/\.?0+$/, '');
}

/** 根据图表标题推断最合适的类别维度 */
function inferCategoryKey(title: string, categoryKeys: string[]): string | null {
  const lower = title.toLowerCase();
  const patterns = [
    { regex: /战区/, fallback: '战区' },
    { regex: /省办/, fallback: '省办' },
    { regex: /客户/, fallback: '客户名称' },
    { regex: /人员|执行人|员工/, fallback: '执行人' },
    { regex: /营业所/, fallback: '营业所' },
    { regex: /渠道/, fallback: '渠道' },
  ];
  for (const p of patterns) {
    if (p.regex.test(lower)) {
      const exact = categoryKeys.find((k) => k === p.fallback);
      if (exact) return exact;
      const fuzzy = categoryKeys.find((k) => p.regex.test(k));
      if (fuzzy) return fuzzy;
    }
  }
  return categoryKeys[0] || null;
}

/** 根据图表标题推断最合适的数值指标 */
function inferValueKey(title: string, numericKeys: string[]): string | null {
  const lower = title.toLowerCase();

  // 1) 标题直接包含列名（最准），但排除排序/序号类非指标列
  const ignoreDirectMatch = new Set(['排名', '序号', 'id', '编号']);
  for (const k of numericKeys) {
    if (ignoreDirectMatch.has(k)) continue;
    if (lower.includes(k.toLowerCase())) return k;
  }

  // 2) 语义匹配
  const rules = [
    { regex: /达成率|成交率|达标率|完成率|异常率|达成/, find: (k: string) => /达成率|成交率|达标率|完成率|异常率|达成/.test(k) },
    { regex: /得分|指数|评分/, find: (k: string) => /得分|指数|评分/.test(k) },
    { regex: /金额|产出|销售额|出库|收入/, find: (k: string) => /金额|产出|销售额|出库|收入/.test(k) },
    { regex: /次数|频次/, find: (k: string) => /次数|频次/.test(k) },
    { regex: /门店数|客户数|人数|网点数/, find: (k: string) => /门店数|客户数|人数|网点数/.test(k) },
    { regex: /占比|结构|分布/, find: (k: string) => k === '占比' || /金额|产出/.test(k) },
    { regex: /天数|周转/, find: (k: string) => /天数|周转/.test(k) },
  ];
  for (const rule of rules) {
    if (rule.regex.test(lower)) {
      const key = numericKeys.find(rule.find);
      if (key) return key;
    }
  }

  // 3) 排名类：避免选到「排名」「监控客户数」等，优先得分/指数/率
  if (/排名/.test(lower)) {
    const key = numericKeys.find((k) => /得分|指数|率/.test(k) && !/排名/.test(k));
    if (key) return key;
  }

  return numericKeys[0] || null;
}

/** 判断是否需要按类别维度聚合 */
function shouldAggregate(data: Record<string, unknown>[], categoryKey: string): boolean {
  const values = data.map((d) => String(d[categoryKey]));
  const unique = new Set(values);
  return unique.size < data.length && unique.size > 1;
}

/** 按类别维度聚合：比率/得分/指数用平均，金额/次数/数量用求和 */
function aggregateByKey(
  data: Record<string, unknown>[],
  categoryKey: string,
  valueKey: string,
): { name: string; value: number }[] {
  const groups = new Map<string, { sum: number; count: number }>();
  const isRate = /率|得分|指数|评分|占比|天数|周转/.test(valueKey);

  for (const row of data) {
    const name = String(row[categoryKey] ?? '未分类');
    const value = Number(row[valueKey]) || 0;
    if (!groups.has(name)) {
      groups.set(name, { sum: 0, count: 0 });
    }
    const g = groups.get(name)!;
    g.sum += value;
    g.count += 1;
  }

  return Array.from(groups.entries()).map(([name, g]) => ({
    name,
    value: isRate ? (g.count ? g.sum / g.count : 0) : g.sum,
  }));
}

function buildOption(chart: ChartData, containerWidth = 800): echarts.EChartsOption {
  const raw = chart.data;
  const isMobile = containerWidth < 480;

  if (!raw || (Array.isArray(raw) && raw.length === 0)) {
    return emptyOption();
  }

  // 对象类型：简单的 key-value 聚合
  if (!Array.isArray(raw) && typeof raw === 'object') {
    const entries = Object.entries(raw).filter(([, v]) => typeof v === 'number');
    if (entries.length === 0) return emptyOption();
    return entries.length > 10
      ? barRankOption(
          entries.map(([k, v]) => ({ name: k, value: v as number })),
          isMobile,
        )
      : pieOption(
          entries.map(([k, v]) => ({ name: k, value: v as number })),
          isMobile,
        );
  }

  const data = raw as Record<string, unknown>[];
  if (data.length === 0) return emptyOption();

  const keys = Object.keys(data[0]);
  const numericKeys = keys.filter((k) => data.every((d) => typeof d[k] === 'number'));
  const categoryKeys = keys.filter((k) => !numericKeys.includes(k));

  // 1) 漏斗图：阶段 + 门店数
  if (keys.includes('阶段') && keys.includes('门店数')) {
    return funnelOption(
      data.map((d) => ({ name: String(d['阶段']), value: Number(d['门店数']) || 0 })),
      isMobile,
    );
  }

  // 2) 执行质量：客户维度明细，按正常陈列达标率取 Top 客户排名
  if (
    chart.title?.includes('执行质量') &&
    keys.includes('客户名称') &&
    keys.includes('正常陈列达标率')
  ) {
    return qualityOption(data, isMobile);
  }

  // 3) 散点矩阵：风险-价值
  if (keys.includes('平均风险得分') && keys.includes('平均价值得分')) {
    return scatterOption(data, '平均风险得分', '平均价值得分', '省办', isMobile);
  }

  // 3) 时间序列优先于饼图：单系列折线 / 多系列折线
  const timeKey = keys.find((k) => isTimeKey(k));
  if (timeKey && looksLikeDate(data.map((d) => d[timeKey]))) {
    const valueKeys = numericKeys.filter((k) => k !== timeKey);
    const nonTimeCategoryKeys = categoryKeys.filter((k) => k !== timeKey);
    // 多系列时间序列：时间键 + 另一个类别键 + 数值键（如 客户名称+日期_str+出库金额）
    if (nonTimeCategoryKeys.length === 1 && valueKeys.length === 1) {
      const seriesKey = nonTimeCategoryKeys[0];
      const valueKey = valueKeys[0];
      const seriesNames = [...new Set(data.map((d) => String(d[seriesKey])))];
      // 系列数量 >1 就做 pivot；超过 12 个系列时只取累计值最大的前 12
      if (seriesNames.length > 1) {
        return multiLineOption(data, timeKey, seriesKey, valueKey, isMobile, 12);
      }
    }
    if (valueKeys.length > 0) {
      return lineOption(data, timeKey, valueKeys, isMobile);
    }
  }

  // 4) 结构/占比：饼图 或 横向条形图
  if (keys.includes('占比') && categoryKeys.length > 0) {
    const categoryKey = inferCategoryKey(chart.title, categoryKeys) || categoryKeys[0];
    const valueKey = inferValueKey(chart.title, numericKeys) || numericKeys[0];
    const pieData = data.map((d) => ({ name: String(d[categoryKey]), value: Number(d[valueKey]) || 0 }));
    if (data.length <= 10) {
      return pieOption(pieData, isMobile);
    }
    return barRankOption(pieData, isMobile);
  }

  // 5) 排名/结构条形图：类别 + 数值（取前 15，明细时自动聚合）
  if (categoryKeys.length > 0 && numericKeys.length > 0) {
    const categoryKey = inferCategoryKey(chart.title, categoryKeys) || categoryKeys[0];
    const valueKey = inferValueKey(chart.title, numericKeys) || numericKeys[0];
    const plotData = shouldAggregate(data, categoryKey)
      ? aggregateByKey(data, categoryKey, valueKey)
      : data.map((d) => ({ name: String(d[categoryKey]), value: Number(d[valueKey]) || 0 }));
    return barRankOption(plotData, isMobile);
  }

  // 6) 默认柱状图
  return defaultBarOption(data, isMobile);
}

function emptyOption(): echarts.EChartsOption {
  return {
    title: { text: '暂无数据', left: 'center', top: 'center', textStyle: { color: TEXT_MUTED, fontSize: 14 } },
    backgroundColor: 'transparent',
  };
}

function pieOption(
  pieData: { name: string; value: number }[],
  isMobile = false,
): echarts.EChartsOption {
  // 移动端：图例放底部、标签只显示百分比，避免名称长导致 outside 标签被截断/重叠
  return {
    backgroundColor: 'transparent',
    tooltip: {
      trigger: 'item',
      formatter: (params: unknown) => {
        const p = params as { name: string; value: number; percent: number };
        return `${p.name}<br/>${fmtNum(p.value)} (${p.percent}%)`;
      },
    },
    legend: {
      type: 'scroll',
      orient: isMobile ? 'horizontal' : 'vertical',
      left: isMobile ? 'center' : 0,
      top: isMobile ? 'bottom' : 'middle',
      align: isMobile ? 'auto' : 'left',
      textStyle: { color: TEXT_MUTED, fontSize: isMobile ? 10 : 11 },
      pageIconColor: TEXT_MAIN,
      pageTextStyle: { color: TEXT_MUTED },
    },
    series: [
      {
        type: 'pie',
        radius: isMobile ? ['30%', '48%'] : ['38%', '62%'],
        center: isMobile ? ['50%', '48%'] : ['58%', '50%'],
        data: pieData,
        minAngle: isMobile ? 3 : 2,
        avoidLabelOverlap: true,
        label: {
          show: true,
          position: 'outside',
          color: TEXT_MAIN,
          fontSize: isMobile ? 9 : 11,
          formatter: isMobile ? '{d}%' : '{b}\n{d}%',
          minMargin: 4,
        },
        labelLine: {
          length: isMobile ? 6 : 10,
          length2: isMobile ? 4 : 8,
          lineStyle: { color: BORDER },
        },
        emphasis: {
          itemStyle: { shadowBlur: 4, shadowOffsetX: 0, shadowColor: 'rgba(0,0,0,0.3)' },
        },
        itemStyle: {
          borderRadius: 4,
          borderColor: '#0b0f17',
          borderWidth: 2,
        },
      },
    ],
  };
}

function funnelOption(data: { name: string; value: number }[], isMobile = false): echarts.EChartsOption {
  const max = Math.max(...data.map((d) => d.value), 1);
  return {
    backgroundColor: 'transparent',
    tooltip: { trigger: 'item', formatter: '{b}: {c}' },
    series: [
      {
        type: 'funnel',
        left: '10%',
        top: isMobile ? 10 : 20,
        bottom: isMobile ? 10 : 20,
        width: '80%',
        min: 0,
        max,
        label: { show: true, position: 'inside', color: '#fff', fontSize: isMobile ? 10 : 12 },
        data: data.map((d, i) => ({
          ...d,
          itemStyle: { color: COLOR_PALETTE[i % COLOR_PALETTE.length] },
        })),
      },
    ],
  };
}

function lineOption(
  data: Record<string, unknown>[],
  timeKey: string,
  valueKeys: string[],
  isMobile = false,
): echarts.EChartsOption {
  const xAxis = data.map((d) => String(d[timeKey]));
  const needsZoom = xAxis.length > 12;

  return {
    backgroundColor: 'transparent',
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'line', lineStyle: { color: TEXT_MUTED } },
      formatter: (params: unknown) => {
        const arr = params as { seriesName: string; value: number; name: string; color: string }[];
        const head = `<div style="font-weight:600;margin-bottom:4px;">${arr[0]?.name}</div>`;
        const body = arr
          .map(
            (p) =>
              `<div style="display:flex;align-items:center;gap:6px;"><span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${p.color};"></span><span>${p.seriesName}: ${fmtNum(p.value)}</span></div>`,
          )
          .join('');
        return head + body;
      },
    },
    legend: {
      top: 0,
      type: valueKeys.length > 3 || isMobile ? 'scroll' : 'plain',
      textStyle: { color: TEXT_MUTED, fontSize: isMobile ? 10 : 11 },
      pageIconColor: TEXT_MAIN,
    },
    grid: { left: '3%', right: '4%', bottom: needsZoom ? (isMobile ? 60 : 45) : '3%', top: 30, containLabel: true },
    xAxis: {
      type: 'category',
      data: xAxis,
      axisLine: { lineStyle: { color: BORDER } },
      axisLabel: { color: TEXT_MUTED, fontSize: isMobile ? 9 : 11, rotate: isMobile ? 45 : 0 },
    },
    yAxis: {
      type: 'value',
      splitLine: { lineStyle: { color: GRID_LINE } },
      axisLabel: { color: TEXT_MUTED, fontSize: isMobile ? 9 : 11, formatter: (v: number) => fmtNum(v) },
    },
    dataZoom: needsZoom
      ? [
          { type: 'inside', start: Math.max(0, 100 - Math.floor((12 / xAxis.length) * 100)), end: 100 },
          {
            type: 'slider',
            start: Math.max(0, 100 - Math.floor((12 / xAxis.length) * 100)),
            end: 100,
            height: isMobile ? 20 : 18,
            bottom: isMobile ? 10 : 8,
            borderColor: BORDER,
            fillerColor: 'rgba(79,70,229,0.2)',
            handleStyle: { color: '#4f46e5' },
            textStyle: { color: TEXT_MUTED, fontSize: isMobile ? 9 : 10 },
          },
        ]
      : undefined,
    series: valueKeys.map((key, idx) => {
      const color = COLOR_PALETTE[idx % COLOR_PALETTE.length];
      return {
        name: key,
        type: 'line',
        smooth: true,
        symbol: 'circle',
        symbolSize: isMobile ? 4 : 6,
        data: data.map((d) => Number(d[key]) || 0),
        itemStyle: { color },
        lineStyle: { width: isMobile ? 2 : 3 },
        areaStyle:
          valueKeys.length === 1
            ? {
                color: {
                  type: 'linear',
                  x: 0,
                  y: 0,
                  x2: 0,
                  y2: 1,
                  colorStops: [
                    { offset: 0, color: `${color}66` },
                    { offset: 1, color: `${color}0D` },
                  ],
                },
              }
            : undefined,
      };
    }),
  };
}

function multiLineOption(
  data: Record<string, unknown>[],
  timeKey: string,
  seriesKey: string,
  valueKey: string,
  isMobile = false,
  topN?: number,
): echarts.EChartsOption {
  const timeValues = [...new Set(data.map((d) => String(d[timeKey])))];
  let seriesNames = [...new Set(data.map((d) => String(d[seriesKey])))];
  const seriesMap = new Map<string, Map<string, number>>();

  for (const name of seriesNames) {
    seriesMap.set(name, new Map(timeValues.map((t) => [t, 0])));
  }
  for (const row of data) {
    const name = String(row[seriesKey]);
    const t = String(row[timeKey]);
    const v = Number(row[valueKey]) || 0;
    seriesMap.get(name)?.set(t, (seriesMap.get(name)?.get(t) || 0) + v);
  }

  // 系列过多时只保留累计值最大的前 N 个
  if (topN && seriesNames.length > topN) {
    seriesNames = seriesNames
      .map((name) => ({ name, total: [...(seriesMap.get(name)?.values() || [])].reduce((a, b) => a + b, 0) }))
      .sort((a, b) => b.total - a.total)
      .slice(0, topN)
      .map((x) => x.name);
  }

  const needsZoom = timeValues.length > 12;

  return {
    backgroundColor: 'transparent',
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'line' },
      formatter: (params: unknown) => {
        const arr = params as { seriesName: string; value: number; name: string; color: string }[];
        const head = `<div style="font-weight:600;margin-bottom:4px;">${arr[0]?.name}</div>`;
        const body = arr
          .map(
            (p) =>
              `<div style="display:flex;align-items:center;gap:6px;"><span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${p.color};"></span><span>${p.seriesName}: ${fmtNum(p.value)}</span></div>`,
          )
          .join('');
        return head + body;
      },
    },
    legend: {
      top: 0,
      type: seriesNames.length > 3 || isMobile ? 'scroll' : 'plain',
      textStyle: { color: TEXT_MUTED, fontSize: isMobile ? 10 : 11 },
      pageIconColor: TEXT_MAIN,
    },
    grid: { left: '3%', right: '4%', bottom: needsZoom ? (isMobile ? 60 : 45) : '3%', top: 30, containLabel: true },
    xAxis: {
      type: 'category',
      data: timeValues,
      axisLine: { lineStyle: { color: BORDER } },
      axisLabel: { color: TEXT_MUTED, fontSize: isMobile ? 9 : 11, rotate: isMobile ? 45 : 0 },
    },
    yAxis: {
      type: 'value',
      splitLine: { lineStyle: { color: GRID_LINE } },
      axisLabel: { color: TEXT_MUTED, fontSize: isMobile ? 9 : 11, formatter: (v: number) => fmtNum(v) },
    },
    dataZoom: needsZoom
      ? [
          { type: 'inside', start: Math.max(0, 100 - Math.floor((12 / timeValues.length) * 100)), end: 100 },
          {
            type: 'slider',
            start: Math.max(0, 100 - Math.floor((12 / timeValues.length) * 100)),
            end: 100,
            height: isMobile ? 20 : 18,
            bottom: isMobile ? 10 : 8,
            borderColor: BORDER,
            fillerColor: 'rgba(79,70,229,0.2)',
            handleStyle: { color: '#4f46e5' },
            textStyle: { color: TEXT_MUTED, fontSize: isMobile ? 9 : 10 },
          },
        ]
      : undefined,
    series: seriesNames.map((name, idx) => {
      const color = COLOR_PALETTE[idx % COLOR_PALETTE.length];
      return {
        name,
        type: 'line',
        smooth: true,
        symbol: 'circle',
        symbolSize: isMobile ? 4 : 6,
        data: timeValues.map((t) => seriesMap.get(name)?.get(t) || 0),
        itemStyle: { color },
        lineStyle: { width: isMobile ? 2 : 3 },
      };
    }),
  };
}

function barRankOption(
  data: { name: string; value: number }[],
  isMobile = false,
): echarts.EChartsOption {
  const sorted = [...data].sort((a, b) => b.value - a.value).slice(0, isMobile ? 8 : 15);
  const yAxis = sorted.map((d) => d.name).reverse();
  const series = sorted.map((d) => d.value).reverse();

  return {
    backgroundColor: 'transparent',
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: (params: unknown) => {
        const p = (params as { name: string; value: number }[])[0];
        return `${p.name}<br/>${fmtNum(p.value)}`;
      },
    },
    grid: {
      left: '3%',
      right: isMobile ? '4%' : '8%',
      bottom: isMobile ? '16%' : '8%',
      top: '3%',
      containLabel: true,
    },
    xAxis: {
      type: 'value',
      splitNumber: isMobile ? 4 : undefined,
      splitLine: { lineStyle: { color: GRID_LINE } },
      axisLabel: {
        color: TEXT_MUTED,
        fontSize: isMobile ? 9 : 11,
        formatter: (v: number) => fmtNum(v),
      },
    },
    yAxis: {
      type: 'category',
      data: yAxis,
      axisLine: { lineStyle: { color: BORDER } },
      axisLabel: {
        color: TEXT_MUTED,
        fontSize: isMobile ? 9 : 11,
        width: isMobile ? 90 : 120,
        overflow: 'truncate',
      },
    },
    series: [
      {
        name: 'value',
        type: 'bar',
        data: series,
        barMaxWidth: isMobile ? 12 : 24,
        itemStyle: {
          borderRadius: [0, 4, 4, 0],
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 1,
            y2: 0,
            colorStops: [
              { offset: 0, color: '#4f46e5' },
              { offset: 1, color: '#0ea5e9' },
            ],
          },
        },
        label: {
          show: !isMobile,
          position: 'right',
          color: TEXT_MAIN,
          fontSize: 10,
          formatter: (p: unknown) => fmtNum(Number((p as { value: number }).value)),
        },
      },
    ],
  };
}

/** 执行质量：按省办聚合加权平均，展示各省办平均正常陈列达标率 */
function qualityOption(data: Record<string, unknown>[], isMobile = false): echarts.EChartsOption {
  const agg = new Map<
    string,
    { 正常陈列达标率_sum: number; 必上架达成率_sum: number; 执行门店数: number }
  >();

  for (const row of data) {
    const province = String(row['省办']);
    const stores = Number(row['执行门店数']) || 0;
    if (!agg.has(province)) {
      agg.set(province, { 正常陈列达标率_sum: 0, 必上架达成率_sum: 0, 执行门店数: 0 });
    }
    const item = agg.get(province)!;
    item.正常陈列达标率_sum += (Number(row['正常陈列达标率']) || 0) * stores;
    item.必上架达成率_sum += (Number(row['必上架达成率']) || 0) * stores;
    item.执行门店数 += stores;
  }

  const sorted = Array.from(agg.entries())
    .map(([name, v]) => ({
      name,
      正常陈列达标率: v.执行门店数 ? v.正常陈列达标率_sum / v.执行门店数 : 0,
      必上架达成率: v.执行门店数 ? v.必上架达成率_sum / v.执行门店数 : 0,
      执行门店数: v.执行门店数,
    }))
    .sort((a, b) => a.正常陈列达标率 - b.正常陈列达标率)
    .slice(0, isMobile ? 10 : 15);

  const yAxis = sorted.map((d) => d.name);
  const series = sorted.map((d) => d.正常陈列达标率);

  return {
    backgroundColor: 'transparent',
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: (params: unknown) => {
        const p = (params as { name: string; value: number }[])[0];
        const row = sorted.find((r) => r.name === p.name);
        return `${p.name}<br/>正常陈列达标率: ${(p.value * 100).toFixed(1)}%<br/>必上架达成率: ${(row?.必上架达成率 || 0).toFixed(2)}<br/>执行门店数: ${row?.执行门店数 || 0}`;
      },
    },
    grid: {
      left: '3%',
      right: isMobile ? '8%' : '12%',
      bottom: isMobile ? '10%' : '3%',
      top: '3%',
      containLabel: true,
    },
    xAxis: {
      type: 'value',
      max: 1,
      splitNumber: isMobile ? 5 : undefined,
      axisLabel: {
        color: TEXT_MUTED,
        fontSize: isMobile ? 9 : 11,
        formatter: (v: number) => `${(v * 100).toFixed(0)}%`,
      },
      splitLine: { lineStyle: { color: GRID_LINE } },
    },
    yAxis: {
      type: 'category',
      data: yAxis,
      axisLine: { lineStyle: { color: BORDER } },
      axisLabel: {
        color: TEXT_MUTED,
        fontSize: isMobile ? 8 : 10,
        width: isMobile ? 90 : 120,
        overflow: 'truncate',
      },
    },
    series: [
      {
        type: 'bar',
        data: series,
        barMaxWidth: isMobile ? 14 : 20,
        itemStyle: {
          borderRadius: [0, 4, 4, 0],
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 1,
            y2: 0,
            colorStops: [
              { offset: 0, color: '#4f46e5' },
              { offset: 1, color: '#0ea5e9' },
            ],
          },
        },
        label: {
          show: !isMobile,
          position: 'right',
          color: TEXT_MAIN,
          fontSize: 10,
          formatter: (p: unknown) => `${(((p as { value: number }).value) * 100).toFixed(0)}%`,
        },
      },
    ],
  };
}

function scatterOption(
  data: Record<string, unknown>[],
  xKey: string,
  yKey: string,
  nameKey: string,
  isMobile = false,
): echarts.EChartsOption {
  const seriesData = data.map((d) => [Number(d[xKey]) || 0, Number(d[yKey]) || 0, String(d[nameKey])]);
  return {
    backgroundColor: 'transparent',
    tooltip: {
      trigger: 'item',
      formatter: (params: unknown) => {
        const p = params as { data: [number, number, string] };
        return `${p.data[2]}<br/>${xKey}: ${p.data[0]}<br/>${yKey}: ${p.data[1]}`;
      },
    },
    grid: { left: '3%', right: '4%', bottom: '3%', top: '8%', containLabel: true },
    xAxis: {
      type: 'value',
      name: xKey,
      nameTextStyle: { color: TEXT_MUTED, fontSize: isMobile ? 10 : 11 },
      splitLine: { lineStyle: { color: GRID_LINE } },
      axisLabel: { color: TEXT_MUTED, fontSize: isMobile ? 9 : 11 },
    },
    yAxis: {
      type: 'value',
      name: yKey,
      nameTextStyle: { color: TEXT_MUTED, fontSize: isMobile ? 10 : 11 },
      splitLine: { lineStyle: { color: GRID_LINE } },
      axisLabel: { color: TEXT_MUTED, fontSize: isMobile ? 9 : 11 },
    },
    series: [
      {
        type: 'scatter',
        data: seriesData,
        symbolSize: isMobile ? 6 : 8,
        itemStyle: {
          color: '#4f46e5',
          shadowBlur: 6,
          shadowColor: 'rgba(79,70,229,0.35)',
        },
      },
    ],
  };
}

function defaultBarOption(data: Record<string, unknown>[], isMobile = false): echarts.EChartsOption {
  const keys = Object.keys(data[0]);
  const categoryKey = keys[0];
  const numericKeys = keys.filter((k) => data.every((d) => typeof d[k] === 'number'));
  const valueKey = numericKeys[0] || keys[1];
  const xAxis = data.map((d) => String(d[categoryKey]));
  const series = data.map((d) => Number(d[valueKey]) || 0);

  return {
    backgroundColor: 'transparent',
    tooltip: { trigger: 'axis' },
    grid: { left: '3%', right: '4%', bottom: '3%', top: '8%', containLabel: true },
    xAxis: {
      type: 'category',
      data: xAxis,
      axisLine: { lineStyle: { color: BORDER } },
      axisLabel: { color: TEXT_MUTED, fontSize: isMobile ? 9 : 11, rotate: isMobile ? 45 : 30 },
    },
    yAxis: {
      type: 'value',
      splitLine: { lineStyle: { color: GRID_LINE } },
      axisLabel: { color: TEXT_MUTED, fontSize: isMobile ? 9 : 11, formatter: (v: number) => fmtNum(v) },
    },
    series: [
      {
        type: 'bar',
        data: series,
        barMaxWidth: isMobile ? 20 : 32,
        itemStyle: {
          borderRadius: [4, 4, 0, 0],
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: '#4f46e5' },
              { offset: 1, color: '#0ea5e9' },
            ],
          },
        },
      },
    ],
  };
}
